import React, { useState, useEffect } from 'react'
import api from '../api'
import Button from '../components/Button'

interface ProfileData {
  date_of_birth: string
  gender: string
  work_status: string
  hours_per_day: string
  role_type: string
  work_pattern: string
  children_count: string
  youngest_child_age: string
  caring_responsibilities: string
  physical_activity: string
  health_affects_energy: boolean | null
  chronotype: string
  motivators: string[]
  relaxation_methods: string[]
  hobbies: string
}

const emptyProfile: ProfileData = {
  date_of_birth: '',
  gender: '',
  work_status: '',
  hours_per_day: '',
  role_type: '',
  work_pattern: '',
  children_count: '',
  youngest_child_age: '',
  caring_responsibilities: '',
  physical_activity: '',
  health_affects_energy: null,
  chronotype: '',
  motivators: [],
  relaxation_methods: [],
  hobbies: '',
}

export default function Profile() {
  const [profile, setProfile] = useState<ProfileData>(emptyProfile)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      const res = await api.get('/profile')
      if (res.data.profile) {
        setProfile({
          ...emptyProfile,
          ...res.data.profile,
          motivators: res.data.profile.motivators || [],
          relaxation_methods: res.data.profile.relaxation_methods || [],
        })
      }
    } catch (err) {
      console.error('Failed to load profile', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.post('/profile', profile)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      console.error('Failed to save profile', err)
    } finally {
      setSaving(false)
    }
  }

  const toggleArray = (field: 'motivators' | 'relaxation_methods', value: string) => {
    setProfile(prev => {
      const arr = prev[field] || []
      return {
        ...prev,
        [field]: arr.includes(value)
          ? arr.filter(v => v !== value)
          : [...arr, value],
      }
    })
  }

  const SelectField = ({
    label,
    field,
    options,
  }: {
    label: string
    field: keyof ProfileData
    options: { value: string; label: string }[]
  }) => (
    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'block', fontSize: '13px',
        fontWeight: 600, color: 'var(--ink)',
        marginBottom: '8px',
      }}>
        {label}
      </label>
      <div style={{
        display: 'flex', flexWrap: 'wrap' as const, gap: '8px',
      }}>
        {options.map(opt => (
          <button
            key={opt.value}
            onClick={() => setProfile(prev => ({ ...prev, [field]: opt.value }))}
            style={{
              padding: '8px 16px', fontSize: '13px',
              borderRadius: 'var(--radius-full)', cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              transition: 'all var(--transition-fast)',
              border: profile[field] === opt.value
                ? '2px solid var(--gold)'
                : '1.5px solid var(--warm-border)',
              background: profile[field] === opt.value
                ? 'rgba(200,150,62,0.1)'
                : 'var(--cream)',
              color: profile[field] === opt.value
                ? 'var(--gold)'
                : 'var(--muted)',
              fontWeight: profile[field] === opt.value ? 600 : 400,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )

  const MultiSelectField = ({
    label,
    field,
    options,
  }: {
    label: string
    field: 'motivators' | 'relaxation_methods'
    options: { value: string; label: string }[]
  }) => (
    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'block', fontSize: '13px',
        fontWeight: 600, color: 'var(--ink)',
        marginBottom: '4px',
      }}>
        {label}
      </label>
      <p style={{
        fontSize: '12px', color: 'var(--muted)', marginBottom: '8px',
      }}>
        Select all that apply
      </p>
      <div style={{
        display: 'flex', flexWrap: 'wrap' as const, gap: '8px',
      }}>
        {options.map(opt => {
          const selected = (profile[field] || []).includes(opt.value)
          return (
            <button
              key={opt.value}
              onClick={() => toggleArray(field, opt.value)}
              style={{
                padding: '8px 16px', fontSize: '13px',
                borderRadius: 'var(--radius-full)', cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                transition: 'all var(--transition-fast)',
                border: selected
                  ? '2px solid var(--gold)'
                  : '1.5px solid var(--warm-border)',
                background: selected
                  ? 'rgba(200,150,62,0.1)'
                  : 'var(--cream)',
                color: selected ? 'var(--gold)' : 'var(--muted)',
                fontWeight: selected ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}>
        <div style={{
          width: '28px', height: '28px',
          border: '3px solid var(--warm-border)',
          borderTopColor: 'var(--gold)', borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    )
  }

  return (
    <div className="animate-fadeUp">

      <h2 style={{ fontFamily: 'var(--font-display)', marginBottom: '4px' }}>
        Your Personal Profile
      </h2>
      <p style={{ color: 'var(--muted)', marginBottom: '8px' }}>
        This helps Velumio personalise your wellbeing score, energy forecast,
        and weekly letter to your real life context.
      </p>
      <div style={{
        fontSize: '12px',
        color: 'var(--green)',
        background: 'var(--green-light)',
        border: '1px solid var(--green)',
        borderRadius: 'var(--radius-md)',
        padding: '10px 14px',
        marginBottom: '32px',
      }}>
        🔒 All information is encrypted and never shared with your employer.
      </div>

      {/* Personal Section */}
      <div style={{
        background: 'var(--white)', borderRadius: 'var(--radius-lg)',
        padding: '28px', border: '1px solid var(--cream-dark)',
        marginBottom: '20px',
      }}>
        <h3 style={{
          fontFamily: 'var(--font-display)', fontSize: '16px',
          marginBottom: '20px', paddingBottom: '12px',
          borderBottom: '1px solid var(--cream-dark)',
        }}>
          Personal
        </h3>

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block', fontSize: '13px',
            fontWeight: 600, color: 'var(--ink)', marginBottom: '8px',
          }}>
            Date of birth
          </label>
          <input
            type="date"
            value={profile.date_of_birth}
            onChange={e => setProfile(prev => ({
              ...prev, date_of_birth: e.target.value
            }))}
            style={{
              padding: '10px 14px', fontSize: '14px',
              border: '1.5px solid var(--warm-border)',
              borderRadius: 'var(--radius-md)', outline: 'none',
              fontFamily: 'var(--font-body)', color: 'var(--ink)',
              background: 'var(--cream)',
            }}
          />
        </div>

        <SelectField
          label="Gender"
          field="gender"
          options={[
            { value: 'male', label: 'Male' },
            { value: 'female', label: 'Female' },
            { value: 'non_binary', label: 'Non-binary' },
            { value: 'prefer_not', label: 'Prefer not to say' },
          ]}
        />
      </div>

      {/* Work Section */}
      <div style={{
        background: 'var(--white)', borderRadius: 'var(--radius-lg)',
        padding: '28px', border: '1px solid var(--cream-dark)',
        marginBottom: '20px',
      }}>
        <h3 style={{
          fontFamily: 'var(--font-display)', fontSize: '16px',
          marginBottom: '20px', paddingBottom: '12px',
          borderBottom: '1px solid var(--cream-dark)',
        }}>
          Work
        </h3>

        <SelectField
          label="Do you currently work?"
          field="work_status"
          options={[
            { value: 'employed', label: 'Yes, employed' },
            { value: 'self_employed', label: 'Self-employed' },
            { value: 'student', label: 'Student' },
            { value: 'not_working', label: 'Not currently working' },
          ]}
        />

        {(profile.work_status === 'employed' ||
          profile.work_status === 'self_employed') && (
          <>
            <SelectField
              label="How many hours per day do you work on average?"
              field="hours_per_day"
              options={[
                { value: 'under_6', label: 'Under 6 hours' },
                { value: '6_8', label: '6 to 8 hours' },
                { value: '8_10', label: '8 to 10 hours' },
                { value: '10_12', label: '10 to 12 hours' },
                { value: 'over_12', label: 'Over 12 hours' },
              ]}
            />

            <SelectField
              label="How would you describe your role?"
              field="role_type"
              options={[
                { value: 'mental', label: 'Mostly mental' },
                { value: 'physical', label: 'Mostly physical' },
                { value: 'mixed', label: 'Equal mix of both' },
              ]}
            />

            <SelectField
              label="What is your work pattern?"
              field="work_pattern"
              options={[
                { value: 'office', label: 'Office full time' },
                { value: 'hybrid', label: 'Hybrid' },
                { value: 'remote', label: 'Fully remote' },
                { value: 'shift', label: 'Shift worker' },
                { value: 'part_time', label: 'Part time' },
              ]}
            />
          </>
        )}
      </div>

      {/* Life Context Section */}
      <div style={{
        background: 'var(--white)', borderRadius: 'var(--radius-lg)',
        padding: '28px', border: '1px solid var(--cream-dark)',
        marginBottom: '20px',
      }}>
        <h3 style={{
          fontFamily: 'var(--font-display)', fontSize: '16px',
          marginBottom: '20px', paddingBottom: '12px',
          borderBottom: '1px solid var(--cream-dark)',
        }}>
          Life Context
        </h3>

        <SelectField
          label="Do you have children?"
          field="children_count"
          options={[
            { value: 'none', label: 'No children' },
            { value: '1', label: '1 child' },
            { value: '2', label: '2 children' },
            { value: '3+', label: '3 or more' },
          ]}
        />

        {profile.children_count && profile.children_count !== 'none' && (
          <SelectField
            label="Age of your youngest child?"
            field="youngest_child_age"
            options={[
              { value: 'under_2', label: 'Under 2' },
              { value: '2_5', label: '2 to 5' },
              { value: '6_12', label: '6 to 12' },
              { value: '13_18', label: '13 to 18' },
              { value: '18+', label: '18 or over' },
            ]}
          />
        )}

        <SelectField
          label="Do you have any caring responsibilities?"
          field="caring_responsibilities"
          options={[
            { value: 'none', label: 'None' },
            { value: 'childcare', label: 'Child care' },
            { value: 'eldercare', label: 'Elder care' },
            { value: 'both', label: 'Both' },
          ]}
        />
      </div>

      {/* Wellbeing Section */}
      <div style={{
        background: 'var(--white)', borderRadius: 'var(--radius-lg)',
        padding: '28px', border: '1px solid var(--cream-dark)',
        marginBottom: '20px',
      }}>
        <h3 style={{
          fontFamily: 'var(--font-display)', fontSize: '16px',
          marginBottom: '20px', paddingBottom: '12px',
          borderBottom: '1px solid var(--cream-dark)',
        }}>
          Wellbeing
        </h3>

        <SelectField
          label="How much physical activity do you do daily?"
          field="physical_activity"
          options={[
            { value: 'none', label: 'None' },
            { value: 'light', label: 'Light (walking)' },
            { value: 'moderate', label: 'Moderate (30 min exercise)' },
            { value: 'active', label: 'Active (1hr+ exercise)' },
          ]}
        />

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block', fontSize: '13px',
            fontWeight: 600, color: 'var(--ink)', marginBottom: '8px',
          }}>
            Do any personal health factors affect your energy levels day to day?
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ].map(opt => (
              <button
                key={String(opt.value)}
                onClick={() => setProfile(prev => ({
                  ...prev, health_affects_energy: opt.value
                }))}
                style={{
                  padding: '8px 24px', fontSize: '13px',
                  borderRadius: 'var(--radius-full)', cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  transition: 'all var(--transition-fast)',
                  border: profile.health_affects_energy === opt.value
                    ? '2px solid var(--gold)'
                    : '1.5px solid var(--warm-border)',
                  background: profile.health_affects_energy === opt.value
                    ? 'rgba(200,150,62,0.1)'
                    : 'var(--cream)',
                  color: profile.health_affects_energy === opt.value
                    ? 'var(--gold)'
                    : 'var(--muted)',
                  fontWeight: profile.health_affects_energy === opt.value ? 600 : 400,
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <SelectField
          label="When do you feel most energetic during the day?"
          field="chronotype"
          options={[
            { value: 'morning', label: 'Morning person' },
            { value: 'afternoon', label: 'Afternoon' },
            { value: 'evening', label: 'Evening' },
            { value: 'varies', label: 'It varies a lot' },
          ]}
        />
      </div>

      {/* Personal Touch Section */}
      <div style={{
        background: 'var(--white)', borderRadius: 'var(--radius-lg)',
        padding: '28px', border: '1px solid var(--cream-dark)',
        marginBottom: '20px',
      }}>
        <h3 style={{
          fontFamily: 'var(--font-display)', fontSize: '16px',
          marginBottom: '8px', paddingBottom: '12px',
          borderBottom: '1px solid var(--cream-dark)',
        }}>
          Personal Touch
        </h3>
        <p style={{
          fontSize: '12px', color: 'var(--muted)', marginBottom: '20px',
        }}>
          This helps personalise your weekly letter, coach prompts,
          and recovery suggestions.
        </p>

        <MultiSelectField
          label="What motivates you most?"
          field="motivators"
          options={[
            { value: 'career', label: 'Career growth' },
            { value: 'family', label: 'Family' },
            { value: 'creative', label: 'Creative work' },
            { value: 'helping', label: 'Helping others' },
            { value: 'financial', label: 'Financial security' },
            { value: 'health', label: 'Personal health' },
          ]}
        />

        <MultiSelectField
          label="How do you usually relax?"
          field="relaxation_methods"
          options={[
            { value: 'exercise', label: 'Exercise' },
            { value: 'reading', label: 'Reading' },
            { value: 'social', label: 'Social time' },
            { value: 'tv_gaming', label: 'TV or Gaming' },
            { value: 'walking', label: 'Walking' },
            { value: 'music', label: 'Music' },
            { value: 'creative', label: 'Creative hobbies' },
            { value: 'sleep', label: 'Sleep or rest' },
          ]}
        />

        <div>
          <label style={{
            display: 'block', fontSize: '13px',
            fontWeight: 600, color: 'var(--ink)', marginBottom: '8px',
          }}>
            Do you have any hobbies you regularly enjoy?
            <span style={{
              fontWeight: 400, color: 'var(--muted)', marginLeft: '8px',
            }}>
              (optional)
            </span>
          </label>
          <textarea
            value={profile.hobbies}
            onChange={e => setProfile(prev => ({
              ...prev, hobbies: e.target.value
            }))}
            placeholder="e.g. photography, cooking, football, painting..."
            style={{
              width: '100%', minHeight: '80px',
              padding: '12px 14px', fontSize: '14px',
              border: '1.5px solid var(--warm-border)',
              borderRadius: 'var(--radius-md)', outline: 'none',
              fontFamily: 'var(--font-body)', color: 'var(--ink)',
              resize: 'vertical' as const, lineHeight: 1.6,
            }}
          />
        </div>
      </div>

      {/* Save Button */}
      <div style={{
        display: 'flex', alignItems: 'center',
        gap: '16px', marginBottom: '40px',
      }}>
        <Button
          size="md"
          loading={saving}
          onClick={handleSave}
        >
          Save profile
        </Button>
        {saved && (
          <span style={{ fontSize: '14px', color: 'var(--green)' }}>
            ✓ Profile saved — your scores will now reflect your life context
          </span>
        )}
      </div>

    </div>
  )
}